import {
    getSessionStorageItemJson,
    setSessionStorageItemJson,
} from '@vfde-sails/storage';
import {
    debugInfo,
    getExpirationDate,
} from '@vfde-sails/utils';
import { SessionAppState } from '../interface';
import { AppState } from '../container/App/interface';
import { initialState } from '../container/App/reducer';
import { State } from '../container/App/selector';
import { STORAGE_KEY } from '../constant';
import { getCMSConfig } from './cms';
import { Namespace } from '../enum';

const APP_SESSION_KEY = `${Namespace.App}`;

/**
 * Updates the state from the CMS options on pageload and
 * sets `initialLevelId`.
 * @param draftState - State to be modified
 * @returns Modified state
 */
const getStateFromPageOptions = (draftState: AppState) => {
    const { initialLevelId } = getCMSConfig();

    draftState.currentLevelId = initialLevelId;

    return draftState;
};

/**
 * Updates the state from the saved session on pageload
 * Does not update the state if the session state is expired
 * @param draftState - State to be modified
 * @returns Modified state
 */
const getStateFromSession = (draftState: AppState) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);
    const appState: Partial<AppState> | undefined = sessionState?.[APP_SESSION_KEY];

    if (!sessionState || !appState) {
        debugInfo('Failed to load state from session storage.');

        return draftState;
    }

    if (new Date(sessionState[APP_SESSION_KEY].expiry) < new Date()) {
        debugInfo('Session expired!');

        return draftState;
    }

    return { ...draftState, ...appState };
};

/**
 * Loads state from sessionStorage, additionalPageOptions, query params & cookies.
 * @returns AppState
 */
export const loadInitialAppState = (): Partial<AppState> => {
    try {
        // Note: We need a deep copy of the initialState, becasue 'immer' makes it read-only.
        let draftState: AppState = JSON.parse(JSON.stringify(initialState));

        draftState = getStateFromPageOptions(draftState);
        // draftState = getStateFromSession(draftState);

        return draftState;
    }
    catch {
        debugInfo('Error while parsing app state.');

        return initialState;
    }
};

/**
 * Saves the AppState to the session, while adding an expiry date of 1 day.
 * @param state - State to be saved
 * TODO: check if do we need store anything in this app -> if not: remove this file
 */
export const saveStateToSession = (state: State) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);

    const sessionAppState: SessionAppState = {
        ...sessionState,
        [APP_SESSION_KEY]: {
            ...state,
            expiry: getExpirationDate(1).toISOString(),
        },
    };

    setSessionStorageItemJson(STORAGE_KEY, sessionAppState);
};
